import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { getIsPermittedWithAnyOfThesePermissions, getIsPermittedWithNoneOfThesePermissions } from '../common/classes/permissions';
import MainMenuItem from './mainMenuItem';
import AdminMenu from './adminMenu';
import checkCabinetPermissions from './headerUtils';
import appHistory from '../../utils/appHistory';
const FixedHeader = _ref => {
  var _navMenuItems$;
  let user = _ref.user,
    _ref$cabinets = _ref.cabinets,
    cabinets = _ref$cabinets === void 0 ? [] : _ref$cabinets,
    _ref$dashboards = _ref.dashboards,
    dashboards = _ref$dashboards === void 0 ? [] : _ref$dashboards,
    settingsLoaded = _ref.settingsLoaded;
  const navMenuItems = [];
  let fileCabinetAvailable = false;

  // show file cabinet unless 'deny_report_view_list' is set
  if (getIsPermittedWithNoneOfThesePermissions(['deny_report_view_list', 'deny_report_*'], user)) {
    navMenuItems.push(/*#__PURE__*/React.createElement(MainMenuItem, {
      className: "t-h-nav-file-cabinet",
      key: "reportsTab",
      href: "/#/file-cabinet",
      glyph: "folder-open",
      title: "File Cabinet"
    }));
    fileCabinetAvailable = true;
  }

  // issues tab will display if `issue_show` is set and not 'deny_issue_view_list'
  if (getIsPermittedWithNoneOfThesePermissions(['deny_issue_view_list', 'deny_issue_*'], user) && getIsPermittedWithAnyOfThesePermissions(['issue_show'], user)) {
    navMenuItems.push(/*#__PURE__*/React.createElement(MainMenuItem, {
      className: "t-h-nav-issues",
      key: "issuesTab",
      href: "/#/issues",
      glyph: "th",
      title: "Issues"
    }));
  }
  if ((cabinets === null || cabinets === void 0 ? void 0 : cabinets.length) > 0) {
    cabinets.filter(cabinet => {
      var _cabinet$client;
      return cabinet === null || cabinet === void 0 || (_cabinet$client = cabinet.client) === null || _cabinet$client === void 0 ? void 0 : _cabinet$client.web;
    }).forEach(cabinet => {
      const identifier = cabinet.identifier,
        title = cabinet.title,
        _cabinet$client2 = cabinet.client,
        _cabinet$client3 = _cabinet$client2 === void 0 ? {} : _cabinet$client2,
        _cabinet$client3$web = _cabinet$client3.web,
        _cabinet$client3$web2 = _cabinet$client3$web === void 0 ? {} : _cabinet$client3$web,
        _cabinet$client3$web3 = _cabinet$client3$web2.layout,
        _cabinet$client3$web4 = _cabinet$client3$web3 === void 0 ? {} : _cabinet$client3$web3,
        superset = _cabinet$client3$web4.superset,
        _cabinet$client3$web5 = _cabinet$client3$web2.icon,
        icon = _cabinet$client3$web5 === void 0 ? 'folder-open' : _cabinet$client3$web5,
        _cabinet$client3$web6 = _cabinet$client3$web2.tabGroup,
        tabGroup = _cabinet$client3$web6 === void 0 ? {} : _cabinet$client3$web6;
      const isValidDashboards = dashboards.some(dashboard => dashboard.name === superset);
      const isInvalidSuperset = superset && !isValidDashboards;
      const hasCabinetPermission = checkCabinetPermissions(cabinet, user);

      // Only add cabinets without tabGroup.identifier
      if (!tabGroup.identifier && !isInvalidSuperset && hasCabinetPermission) {
        navMenuItems.push(/*#__PURE__*/React.createElement(MainMenuItem, {
          className: "t-h-nav-".concat(identifier),
          key: "".concat(identifier, "Tab"),
          href: "/#/cabinets/".concat(identifier),
          glyph: icon,
          title: title
        }));
      }
    });
  }

  // assets tab will display if `asset_admin` is set and not 'read_only'
  if (getIsPermittedWithNoneOfThesePermissions(['read_only'], user) && getIsPermittedWithAnyOfThesePermissions(['asset_admin'], user)) {
    navMenuItems.push(/*#__PURE__*/React.createElement(MainMenuItem, {
      className: "t-h-nav-assets",
      key: "assetsTab",
      href: "/#/assets",
      glyph: "briefcase",
      title: "Asset List"
    }));
  }

  // documents tab will display if not 'read_only' and not 'deny_document_view_list'
  if (getIsPermittedWithNoneOfThesePermissions(['read_only'], user) && getIsPermittedWithNoneOfThesePermissions(['deny_document_view_list', 'deny_document_*'], user)) {
    navMenuItems.push(/*#__PURE__*/React.createElement(MainMenuItem, {
      className: "t-h-nav-library",
      key: "docsTab",
      href: "/#/library",
      glyph: "book",
      title: "Library"
    }));
  }
  navMenuItems.push(/*#__PURE__*/React.createElement(AdminMenu, {
    key: "admin"
  }));
  const firstNavHref = (_navMenuItems$ = navMenuItems[0]) === null || _navMenuItems$ === void 0 || (_navMenuItems$ = _navMenuItems$.props) === null || _navMenuItems$ === void 0 ? void 0 : _navMenuItems$.href;
  const pathname = appHistory.location.pathname;

  // If the file cabinet is not available redirect to the first visible tab. This could be
  // implemented at the route level, but the header is the only component with knowledge of
  // what navigation elements are available. Must run in an effect — doing replace() during
  // render can cascade into "Maximum update depth exceeded" when the Router re-renders.
  useEffect(() => {
    if (!fileCabinetAvailable && settingsLoaded && firstNavHref && (pathname === '/' || pathname === '/logout')) {
      const firstVisibleTab = firstNavHref.replace('/#', '');
      appHistory.replace(firstVisibleTab);
    }
  }, [fileCabinetAvailable, settingsLoaded, firstNavHref, pathname]);
  return navMenuItems;
};
FixedHeader.propTypes = {
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      general: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    denyPermissions: PropTypes.arrayOf(PropTypes.string),
    allowPermissions: PropTypes.arrayOf(PropTypes.string),
    client: PropTypes.shape({
      web: PropTypes.shape({
        layout: PropTypes.shape({
          superset: PropTypes.string
        }),
        icon: PropTypes.string,
        tabGroup: PropTypes.shape({
          identifier: PropTypes.string
        })
      })
    })
  })),
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string
  })),
  settingsLoaded: PropTypes.bool.isRequired
};
export default FixedHeader;